import { Ability } from 'common/core/constants/Ability'
import { AbilityState } from 'common/core/constants/AbilityState'
import errorTrackingService from 'utils/services/errorTrackingService'

import { isAbilitiesUnsafeMap, isLimitBasedAbility, isString } from './guards'
import {
  AbilitiesData,
  AbilitiesMap,
  AbilityLimits,
  CommonAbilityNames,
  LimitBasedAbilityNames,
} from './schemas'

export const getDefaultAbilitiesMap = (): AbilitiesMap =>
  Object.fromEntries(
    Object.values(Ability).map((ability) => {
      if (isLimitBasedAbility(ability)) {
        return [ability, { name: ability, state: AbilityState.LIMITED, used: 0, limit: 0 }]
      }

      return [ability, { name: ability, state: AbilityState.LIMITED }]
    }),
  )

export const parseAbilities = (abilities?: AbilitiesData): AbilitiesMap => {
  if (!abilities) {
    return getDefaultAbilitiesMap()
  }
  if (Array.isArray(abilities)) {
    return abilities.filter(isString).reduce<AbilitiesMap>((map, ability) => {
      Object.assign(map, {
        [ability]: getAbilityDescriptor(ability as Ability.LIVECHAT, AbilityState.ENABLED),
      })

      return map
    }, getDefaultAbilitiesMap())
  }
  if (isAbilitiesUnsafeMap(abilities)) {
    return abilities
  }

  errorTrackingService.logError(`Unknown abilities ${typeof abilities}`, {
    scope: 'account',
    section: 'abilities',
  })

  return getDefaultAbilitiesMap()
}

export function getAbilityDescriptor<AbilityName extends CommonAbilityNames>(
  name: AbilityName,
  state: AbilityState.LIMITED | AbilityState.BLOCKED | AbilityState.ENABLED,
): AbilitiesMap[typeof name]
export function getAbilityDescriptor<AbilityName extends LimitBasedAbilityNames>(
  name: AbilityName,
  state: AbilityState,
  limits: AbilityLimits,
): AbilitiesMap[typeof name]
export function getAbilityDescriptor<AbilityName extends Ability>(
  name: AbilityName,
  state: AbilityState,
  limits?: AbilityLimits,
) {
  if (isLimitBasedAbility(name)) {
    return {
      name,
      state,
      ...limits,
    }
  }

  return {
    name,
    state,
  }
}
